import React, {useEffect} from 'react';
import {
  Button,
  Divider,
  Flex,
  Text,
  Tag,
  TagLabel,
  Skeleton,
  TagProps,
  Tooltip,
  FormControl,
  Textarea,
  FormHelperText,
} from '@chakra-ui/react';
import {ScrollableFlex} from '../../utils/scrolling';
import {WrapperFlex} from '../../utils/scrolling';
import {Box} from '@chakra-ui/react';
import {
  LeftColumnContent,
  RightColumnContent,
  TwoColumnGrid,
} from './components';
import {AgentBuilderPageIndicator} from './AgentBuilderPageIndicator';
import {useAgentNavigation} from './AgentBuilderNav.state';
import {useFieldDefinitionEditorStore} from './AiFieldDefinitionEditor.state';
import {
  useAgentBuilderStore,
  useAgentBuilderServerState,
} from './AgentBuilder.state';
import {BuilderFooter} from './BuilderFooter';
import {BuilderPreviewTable} from './BuilderPreviewTable';
import {AgentBuilderPreviewButton} from './AgentBuilderPreviewButton';
import {useIsEnrichingAgent} from '../../../hooks/api/enrichment';
import {isAiFieldDefinition} from '../../../shared/enrichment';
import {useCustomer} from '../../../hooks/api/metadata';
import {StepContainer} from './StepContainer';
import {ConfigFormLabel} from './ConfigFormLabel';
import {useGetTeamSizeParams} from '../../../hooks/api/agents';
import {Information} from '@carbon/icons-react';
import {RoleSearchCountry} from '../../../shared/countries';
import {getAiFieldModel} from '../../../shared/agent';

export const AgentBuilderEditLegacyTeamSizeFieldStep = () => {
  const {id: agentId, status: agentStatus} = useAgentBuilderServerState();
  const {previewAccountIds} = useAgentBuilderStore();
  const {publishChanges, updateServerState, isSaveable} =
    useFieldDefinitionEditorStore();

  const fieldDefinitions = useCustomer().fieldDefinitions ?? [];
  const teamSizeField = fieldDefinitions
    .filter(isAiFieldDefinition)
    .find((fieldDefinition) => fieldDefinition.agentId?.equals(agentId));

  useEffect(() => {
    if (teamSizeField) {
      updateServerState({
        serverState: teamSizeField,
        updateMode: 'ifNoChanges',
      });
    }
  }, [teamSizeField, updateServerState]);

  const onNavigate = useAgentNavigation({
    mutation: publishChanges,
    saveOnBack: agentStatus === 'draft' && isSaveable(),
  });

  return (
    <StepContainer isSaveable={isSaveable()} onSaveMutation={publishChanges}>
      <TwoColumnGrid>
        <Flex
          borderRight="1px solid"
          borderColor="kgray.200"
          direction="column"
        >
          <Box position="relative" h="100%">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <LeftColumnContent>
                  <AgentBuilderPageIndicator />
                  <TeamSizeFieldEditor />
                </LeftColumnContent>
              </ScrollableFlex>
            </WrapperFlex>
          </Box>

          <Divider />

          <BuilderFooter justifyContent="space-between">
            <Button
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('prev')}
              variant="ghost"
            >
              Back
            </Button>

            <Button
              colorScheme="kbuttonblue"
              isDisabled={!isSaveable()}
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('next')}
            >
              Continue to Review
            </Button>
          </BuilderFooter>
        </Flex>

        <RightColumnContent>
          <Flex alignItems="baseline" justifyContent="space-between" mt="-6px">
            <Text fontSize="xl">Preview</Text>
            <AgentBuilderPreviewButton accountIds={previewAccountIds} />
          </Flex>

          <Box flex="1" position="relative">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <BuilderPreviewTable
                  accountIds={previewAccountIds}
                  fieldDefinition={teamSizeField}
                />
              </ScrollableFlex>
            </WrapperFlex>
          </Box>
        </RightColumnContent>
      </TwoColumnGrid>
    </StepContainer>
  );
};

const StyledTag = ({children, ...rest}: React.PropsWithChildren<TagProps>) => (
  <Tag
    size="sm"
    borderRadius="full"
    variant="solid"
    bgColor="kgray.100"
    color="kgray.400"
    fontWeight={400}
    {...rest}
  >
    <TagLabel>{children}</TagLabel>
  </Tag>
);

const Countries = ({countries}: {countries?: RoleSearchCountry[]}) => {
  if (!countries?.length) {
    return null;
  }

  return (
    <Box mt="4">
      <ConfigFormLabel>
        <Flex align="center" gap={2}>
          Countries
          <Tooltip
            label="The Agent will limit its search to these countries."
            placement="right"
            hasArrow
          >
            <Information size={16} />
          </Tooltip>
        </Flex>
      </ConfigFormLabel>

      <Flex wrap="wrap" gap="2" mt={3}>
        {countries.map((country) => (
          <StyledTag key={country}>{country}</StyledTag>
        ))}
      </Flex>
    </Box>
  );
};

const defaultKeywords = ['engineer', 'eng', 'developer', 'dev', 'software'];
const TeamSizeKeywords = () => {
  const {serverState} = useFieldDefinitionEditorStore();
  const {id: agentId} = useAgentBuilderServerState();
  const {data, refetch} = useGetTeamSizeParams(
    serverState ? {fieldId: serverState.id} : 'skipToken'
  );
  const {data: isEnrichingAgent} = useIsEnrichingAgent({agentId});

  useEffect(() => {
    if (!isEnrichingAgent) {
      refetch();
    }
  }, [isEnrichingAgent, refetch]);

  const keywords = data?.include ?? [];

  return (
    <Box mt="4">
      <ConfigFormLabel>
        <Flex align="center" gap={2}>
          Keywords
          <Tooltip
            label="The Agent will use these title keywords to search and estimate team sizes. Editing your prompt will change the keywords."
            placement="right"
            hasArrow
          >
            <Information size={16} />
          </Tooltip>
        </Flex>
      </ConfigFormLabel>

      <Flex wrap="wrap" gap="2" mt={3}>
        {!keywords.length && (
          <>
            {defaultKeywords.map((keyword) => (
              <Skeleton speed={isEnrichingAgent ? 1 : 0} key={keyword}>
                <StyledTag>{keyword}</StyledTag>
              </Skeleton>
            ))}
          </>
        )}
        {!!keywords.length &&
          keywords.map((keyword) => (
            <StyledTag key={keyword}>{keyword}</StyledTag>
          ))}
      </Flex>
    </Box>
  );
};

const defaultConfig = {
  fieldType: 'legacyTeamSize' as const,
  model: getAiFieldModel(),
  analysisFields: [],
};

const TeamSizeFieldEditor = () => {
  const {config, setConfig} = useFieldDefinitionEditorStore();
  const {id: agentId, status} = useAgentBuilderServerState();
  const {data: isEnrichingAgent} = useIsEnrichingAgent({agentId});
  const isDisabled = status === 'published' || isEnrichingAgent;

  if (config && config.fieldType !== 'legacyTeamSize') {
    return null;
  }

  return (
    <Flex direction="column" gap="6">
      <Text fontSize="xl">Prompt your Team Size Estimator</Text>

      <FormControl isRequired>
        <ConfigFormLabel>Prompt</ConfigFormLabel>
        <Textarea
          isDisabled={isDisabled || !!config?.prompt.generatedParams}
          onChange={(e) =>
            setConfig({
              ...(config ?? defaultConfig),
              prompt: {input: e.target.value},
            })
          }
          rows={5}
          placeholder="Ask your agent a question about team sizes..."
          value={config?.prompt.input ?? ''}
        />
        <FormHelperText textColor="kgray.300">
          Prompting is most successful when you phrase your prompt as a question
          about a single account, like “How many prompt engineers work at this
          company?”
        </FormHelperText>
      </FormControl>

      <Countries countries={config?.countries} />
      <TeamSizeKeywords />
    </Flex>
  );
};
